import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  BrowserRouter,
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import type { Locale, Objective } from '@nabu/shared';
import SignIn, { EmailAccess } from './SignIn';
import Objectives from './Objectives';
import Chat from './Chat';
import Progress from './Progress';
import Schedule from './Schedule';
import Push from './Push';
import Mail from './Mail';
import DeleteAccount from './DeleteAccount';
import Disclosure from './Disclosure';
import NotFound from './NotFound';
import Unsubscribe from './Unsubscribe';
import Console from './admin/Console';
import { watchForUpdate } from './version';
import { DEBUG_HOLD_MS, debugOn, setDebug } from './debug';
import { onServiceWorkerMessage } from './swMessages';
import {
  currentObjective,
  getWindow,
  isError,
  signOut,
  whoAmI,
  type Session,
} from './api';
import { MOVED, ROUTES, SETTINGS_SCHEDULE } from './routes';
import {
  guardedView,
  showsSignedInChrome,
  signInView,
  signedInAs,
  viewerOf,
} from './session';
import {
  I18nContext,
  LOCALES,
  LOCALE_STORAGE_KEY,
  initialLocale,
  translate,
  useI18n,
  type CopyKey,
} from './i18n';

/**
 * Whether this is the installed app rather than a page in a browser.
 *
 * It decides one thing, and it is the first thing a learner sees: what `/` is. The
 * manifest's `start_url` is `/`, so the home-screen icon and a stranger arriving from a
 * link land on exactly the same route — and they want opposite pages. A visitor wants
 * the pitch; somebody who installed this weeks ago wants the conversation, and showing
 * them a paragraph explaining what nabu is every time they open their tutor is the same
 * insult as the sign-in screen that used to flash at people who were already signed in.
 *
 * Read once, at load, because a tab cannot become an installed app while it is open.
 * `navigator.standalone` is the iOS signal and predates `display-mode`; both are checked
 * because Safari is the platform where this matters most and the one whose support for
 * the standard form arrived latest.
 */
const STANDALONE =
  (typeof window !== 'undefined' &&
    window.matchMedia?.('(display-mode: standalone)').matches === true) ||
  (typeof navigator !== 'undefined' &&
    (navigator as Navigator & { standalone?: boolean }).standalone === true);

/**
 * The five sections of the app, in menu order.
 *
 * One list, read by the menu and by nothing else — the routes below are written out
 * because each guards and composes differently, and a table that generated them would
 * hide exactly the differences worth reading.
 */
const SECTIONS: readonly { to: string; label: CopyKey }[] = [
  { to: ROUTES.progress, label: 'nav.progress' },
  { to: ROUTES.plan, label: 'nav.plan' },
  { to: ROUTES.research, label: 'nav.data' },
  { to: ROUTES.settings, label: 'nav.config' },
  { to: ROUTES.profile, label: 'nav.profile' },
];

/**
 * Tells somebody a newer version is deployed, and gives them a way to take it.
 *
 * Leo, 2026-09-15, from an installed iOS app: *"when there is a new version there is no way to
 * refresh, you need to enable a 'refresh for new version' notification."*
 *
 * **In a browser tab this problem does not exist**: a reload picks up the new release, because
 * CloudFront serves the HTML `max-age=0, must-revalidate` while the hashed assets are
 * immutable. Standalone there is no address bar, no reload button and on iOS no dependable
 * pull-to-refresh — and an installed app is never closed, only backgrounded, so it never
 * navigates and can run last week's build indefinitely.
 *
 * At the top of the page rather than a toast. A toast is the wrong shape for something that is
 * not urgent, must not vanish before it is read, and may need to sit there while the learner
 * is mid-answer and does not want to be interrupted. Pushing the content down is honest:
 * something has changed and it is asking for a decision.
 *
 * `location.reload()` and nothing cleverer. No state is worth preserving across it — every
 * screen here is a view of server state — and a reload is the one operation certain to fetch
 * the new `index.html` and with it the new bundle.
 */
function UpdateBanner() {
  const { t } = useI18n();
  const [ready, setReady] = useState(false);

  useEffect(() => watchForUpdate(() => setReady(true)), []);

  if (!ready) return null;
  return (
    <div className="notice" role="status">
      <span>{t('update.ready')}</span>
      <button type="button" className="linky notice-do" onClick={() => location.reload()}>
        {t('update.reload')}
      </button>
    </div>
  );
}

/**
 * The one notice pinned above the page, and there is deliberately only ever one.
 *
 * **The height is a constant the rest of the layout depends on.** `--bar-h` is the bar row
 * plus a notice, the chat computes its box from `--bar-h`, and a wrong number there is the
 * composer below the fold — which is the bug that reached a phone this morning. Two stacked
 * notices would mean measuring, so this picks instead of stacking.
 *
 * A new version outranks the setup nudge because it is the only one that can be acted on in
 * one tap and then stops existing, and because a learner running last week's build may be
 * looking at a nudge that build did not have.
 */
/**
 * Every navigation starts at the top of the new page.
 *
 * Leo: *"Whenever I go into a section from the menu it should take me to the top of that
 * page right now it takes me to some place in the vertical scroll that is not top."*
 *
 * **React Router changes the URL and the tree, and never touches the scroll position** — so
 * the document keeps the offset of the screen you left. It is barely noticeable moving
 * between short pages and badly wrong here, because the sections are long and the one that
 * matters most is the top: going from the bottom of a 69-topic plan to Profile lands you
 * past everything Profile contains. In a browser the address bar and the back button make
 * it obvious what happened; installed, there is nothing on screen to explain it.
 *
 * `useLayoutEffect`, so it happens before the browser paints. In a passive effect the old
 * offset is painted once and then corrected, which is a visible jump rather than a fresh
 * page.
 *
 * `document.scrollingElement` rather than `window.scrollTo`, because iOS standalone is
 * unreliable about the latter — it is the same call `chat/viewport.ts` uses to undo iOS's
 * scroll-on-focus, and having two different answers to "scroll the document" in one app is
 * how one of them stops working quietly.
 *
 * Keyed on `pathname` alone. A query string or a hash changing is the same page saying
 * something about itself — a hash in particular is a request to scroll *somewhere else*,
 * and resetting on it would fight the thing that asked.
 */
function ScrollToTop() {
  const { pathname } = useLocation();

  useLayoutEffect(() => {
    const scroller = document.scrollingElement ?? document.documentElement;
    scroller.scrollTop = 0;
  }, [pathname]);

  return null;
}

/**
 * Taking a tapped notification somewhere that means something.
 *
 * Leo, relaying a learner: *"she clicked the notification and nothing happened… notifications
 * should take you to a meaningful place."*
 *
 * Two failures were behind that and this is the second half. The worker was calling
 * `client.navigate()` before `client.focus()`, and in an installed iOS app `navigate` can
 * throw synchronously — skipping its own `.catch`, killing the handler, and never focusing
 * anything; that is fixed in `public/sw.js`. What is left is where she lands once the app is
 * up, and the worker now asks rather than reloads.
 *
 * **A router navigation, not a page load.** `client.navigate()` would rebuild the application
 * and lose her scroll position — and she tapped this while scrolled up reading a briefing, so
 * her place in the thread is the thing least worth discarding. This is an ordinary in-app
 * transition: instant, and the thread keeps its state.
 *
 * Mounted inside `BrowserRouter` beside `ScrollToTop`, because `useNavigate` needs the router
 * and this belongs to the shell rather than to any one screen — a notification can be tapped
 * from anywhere, including a screen that is not the chat.
 */
function OpenFromNotification() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(
    () =>
      onServiceWorkerMessage((message) => {
        // `push` is news and must not move anybody; only a tap is an instruction. See
        // `SwMessage`.
        if (message.type !== 'open') return;
        const path = message.path ?? '/';
        /*
         * Compared by pathname alone, and the query string is dropped on purpose.
         *
         * The worker appends `?m=<messageId>` for correlation and nothing in this app reads
         * it. Kept in the comparison it would never match — she is on `/chat`, the target is
         * `/chat?m=fin_…` — so every tap while the chat was already open would navigate, and
         * a navigation to the route you are on remounts it. That is the scroll position this
         * whole change exists to preserve, thrown away by the fix for throwing it away.
         *
         * Being already there is not a failure to handle: the same message has already made
         * `Chat` refresh, so the question is on screen, and the pill announces it if she is
         * scrolled up.
         */
        const to = path.split('?')[0] ?? '/';
        if (to === location.pathname) return;
        navigate(to);
      }),
    [navigate, location.pathname],
  );

  return null;
}

function Notices({ signedIn }: { signedIn: boolean }) {
  return (
    <>
      <UpdateBanner />
      {signedIn && <SetupNotice />}
    </>
  );
}

/**
 * Tells a learner still setting up that nothing can be sent to them yet.
 *
 * Leo: *"If i am still defining my objective and plan, I should be told I need to configure
 * my message frequency."* The sequence it prevents is the expensive one — state an objective,
 * wait through research, approve a plan, and then discover that nothing arrives because the
 * one screen nobody visited was the schedule. The reach window is not a preference in that
 * state; it is the thing that decides whether the product does anything at all.
 *
 * **Only while they are still setting up.** Once an objective is `active` the teaching is
 * either arriving or visibly not, and a standing banner over a working product is how a
 * learner learns to read past the notice area. `reason === 'no_window'` is the API's own
 * word for it, read from the progress endpoint rather than a second source of truth.
 */
/**
 * What stage of setting up a learner is at, fetched once for the two things that need it.
 *
 * Deliberately **not** from `/api/progress`: that returns `null` until an objective is
 * running — "a plan awaiting approval is not a plan at nought per cent" — which is exactly
 * the stage both callers are asking about. The objective's status and whether a window has
 * been saved are the two facts, and they come from the two endpoints that own them.
 *
 * `undefined` means "not known yet" and is distinct from every answer. A landing route that
 * treated unknown as one of its cases would redirect somebody to the wrong screen and then
 * bounce them, which reads as the app deciding twice.
 */
interface SetupState {
  status: Objective['status'] | 'none';
  windowConfigured: boolean;
}

function useSetupState(signedIn: boolean): SetupState | undefined {
  const [state, setState] = useState<SetupState>();

  useEffect(() => {
    if (!signedIn) return;
    let alive = true;
    void Promise.all([currentObjective(), getWindow()]).then(([objective, window]) => {
      if (!alive) return;
      // A failed read is not a state. Leaving it undefined keeps the landing on its
      // loading branch rather than sending somebody somewhere on a guess.
      if (isError(objective) || isError(window)) return;
      setState({
        status: objective.objective?.status ?? 'none',
        windowConfigured: window.configured,
      });
    });
    return () => {
      alive = false;
    };
  }, [signedIn]);

  return state;
}

/**
 * Tells a learner still setting up that nothing can be sent to them yet.
 *
 * Leo: *"If i am still defining my objective and plan, I should be told I need to configure
 * my message frequency."* The sequence it prevents is the expensive one — state an objective,
 * wait through research, approve a plan, and then find that nothing arrives because the one
 * screen nobody visited was the schedule. In that state the reach window is not a preference;
 * it is what decides whether the product does anything at all.
 *
 * **It follows an `active` objective too, and that is F-110's change.** The rule used to be
 * "only while they are still setting up", on the reasoning that once an objective is live the
 * teaching is either arriving or visibly not. That reasoning assumed somebody had *pressed*
 * something to make it live — walking past this banner twice on the way. Research now starts
 * the teaching itself, from the first subjects that land, so a learner can acquire a running
 * objective without ever visiting a screen that mentions a schedule; and an `active` objective
 * with no reach window is not "visibly not arriving", it is a product doing nothing at all
 * with everything reporting health. That is this project's signature failure, and this banner
 * is the visible half of refusing to let it be quiet.
 *
 * Still gone the moment a window exists, which is what keeps it from becoming furniture: it is
 * shown only to learners nothing can be sent to.
 */
function SetupNotice() {
  const { t } = useI18n();
  const setup = useSetupState(true);

  if (!setup) return null;
  if (setup.windowConfigured) return null;
  // No objective yet is still too early to be nagged about a schedule — there is nothing a
  // schedule would deliver. Every other state, `active` included, has something waiting.
  if (setup.status === 'none') return null;

  return (
    <div className="notice" role="status">
      <span>{t('setup.window')}</span>
      {/*
       * To the switch it is talking about, not to the top of the screen it lives on (F-143).
       *
       * Leo: *"when i click on set it up for notifactions it just takes me to setting not
       * specifically to the place to set notificatons up."* The configuration screen opens
       * with the objective's target, so "Set it up" used to land somebody two panels above
       * the thing they had just agreed to do, on a page long enough to hide it.
       *
       * The fragment names the reach window and not the notification switch, because the
       * window is what this notice is about and what is missing: it appears only while no
       * window is saved, so a link to the switch would leave the notice standing after the
       * learner did what it asked. The notification switch is the next panel down, which is
       * where somebody who read this as "notifications" needs to be anyway.
       */}
      <Link to={SETTINGS_SCHEDULE} className="linky notice-do">
        {t('setup.window.do')}
      </Link>
    </div>
  );
}

/**
 * Where a signed-in learner lands, decided by how far through setting up they are.
 *
 * Leo: *"the page where I land if Im already in progress is chat, if i am still defining my
 * objective and research it should be research."* The chat is the right home for somebody
 * being taught and the wrong one for somebody who has no curriculum yet — it would be an
 * empty thread with nothing to say and no indication of what to do next.
 *
 * Waits rather than guessing. Rendering a redirect before the answer arrives sends somebody
 * to one screen and then moves them, which reads as the app changing its mind.
 */
function Landing({ signedIn }: { signedIn: boolean }) {
  const setup = useSetupState(signedIn);
  const { t } = useI18n();

  if (setup === undefined) return <p className="muted">{t('landing.deciding')}</p>;

  // Being taught: the conversation is the product.
  if (setup.status === 'active') return <Navigate to={ROUTES.chat} replace />;
  // Nothing stated yet, or a finished objective: the plan screen is where one is stated.
  if (setup.status === 'none' || setup.status === 'done' || setup.status === 'abandoned') {
    return <Navigate to={ROUTES.plan} replace />;
  }
  /*
   * Everything in between — drafting, researching, synthesising, review, shelved — is the
   * research and data screen, because that is where the waiting is visible and where the
   * material that shortens it is added. `review` included: a plan awaiting approval is read
   * beside what it was built from.
   */
  return <Navigate to={ROUTES.research} replace />;
}

/**
 * Locale lives above the router, so switching language never navigates.
 *
 * It is also written to `<html lang>`, which is what a screen reader and
 * Google's crawler read — a page whose visible text is Spanish while its lang
 * attribute claims English is worse than either alone.
 */
function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, next);
    } catch {
      // A remembered choice is a convenience, not a requirement. A browser with
      // site data blocked still gets the language it just asked for.
    }
  }, []);

  const value = useMemo(
    () => ({ locale, setLocale, t: (key: CopyKey) => translate(locale, key) }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

function LocaleSwitch() {
  const { locale, setLocale, t } = useI18n();
  return (
    <div className="locale" role="group" aria-label={t('lang.switch')}>
      {LOCALES.map((code) => (
        <button
          key={code}
          type="button"
          className={code === locale ? 'locale-option is-active' : 'locale-option'}
          aria-pressed={code === locale}
          onClick={() => setLocale(code)}
        >
          {code.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

/**
 * The menu, which opens upwards from the bottom of the screen.
 *
 * **The button is at the top and the menu is at the bottom, and that is deliberate.**
 * The bar has to stay at the top: it is the only thing on the screen that is always in
 * the same place, and a learner who has scrolled four screens into a curriculum needs
 * "where am I, where else can I go" to be where it always was. But this is a phone held
 * in one hand, and a list of destinations hanging off the top edge puts every one of
 * them at the far end of a thumb's reach — the corner people shuffle the phone down
 * their palm to get to. Opening it against the bottom edge costs one stretch to the
 * hamburger and then puts all five sections, and the chat above them, in the arc the
 * thumb already covers. A drawer sliding in from the side is the desktop convention and
 * would put the whole list along the edge instead.
 *
 * It is not a modal and does not claim to be one. There is no focus trap, so there is no
 * `aria-modal`, which would tell a screen reader the rest of the page is unreachable
 * when it is not. Focus moves to the first destination on open and returns to the
 * hamburger on Escape; the scrim is there to catch the tap that means "not this".
 */
function Menu({
  open,
  onDismiss,
  onSignOut,
  email,
}: {
  open: boolean;
  /** Closing by Escape or by the scrim, which puts focus back where it came from. */
  onDismiss: () => void;
  onSignOut: () => void;
  /**
   * The address this account signs in with, when the API could tell us — see the
   * `sheet-who` line below, which is where it is rendered and why.
   */
  email: string | undefined;
}) {
  const { t } = useI18n();
  const firstRef = useRef<HTMLAnchorElement>(null);

  // Opening a menu and leaving focus on the button behind it means a keyboard or
  // screen-reader user has to go looking for what just appeared.
  useEffect(() => {
    if (open) firstRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onDismiss]);

  return (
    <>
      {open && <div className="scrim" onClick={onDismiss} aria-hidden="true" />}
      {/*
       * `hidden` rather than a class, so a closed menu is not in the tab order and not
       * in the accessibility tree. A menu that is only invisible is a list of links a
       * screen-reader user walks through on every page.
       */}
      <nav className="sheet" id="menu" aria-label={t('nav.sections')} hidden={!open}>
        {/*
         * Any tap inside the list closes the menu, not only one that navigates.
         *
         * The route change closes it too, one level up — but tapping the section you are
         * already on changes no route, fires no effect, and would leave the menu sitting
         * open over the page it was asked to show. Which is the tap somebody makes when
         * they are lost, and the one that must not look like nothing happened.
         */}
        <ul className="sheet-list" onClick={onDismiss}>
          {/*
           * The conversation, above the five sections and separated from them.
           *
           * It is not a sixth section. It is where the learner spends nearly all of
           * their time, so it is both the first thing in the menu and a control in the
           * bar that needs no menu at all — one tap from anywhere, by the requirement,
           * and the menu entry is what makes it findable by somebody who does not yet
           * know the bar button is a link.
           */}
          <li>
            <NavLink ref={firstRef} to="/chat" className="sheet-item sheet-chat">
              {t('nav.chat')}
            </NavLink>
          </li>
          {SECTIONS.map((section) => (
            <li key={section.to}>
              <NavLink to={section.to} className="sheet-item">
                {t(section.label)}
              </NavLink>
            </li>
          ))}
        </ul>

        {/*
         * Which account this is, above the door out of it (F-140).
         *
         * Leo: *"I cant tell whether im logged in or not, nor with what email."* Sign-in is a
         * code mailed to an address, so somebody with two addresses genuinely has two
         * accounts — with two sets of objectives, two plans and two schedules — and until
         * now nothing on any screen said which one was open. It sits with the sign-out
         * button because they are the same subject: this account, and leaving it.
         *
         * Without an address it says only that you are signed in, and never invents a
         * stand-in: `/api/auth/me` answers "signed in" without one when the row cannot be
         * read, and an account made by the retired phone channel may hold none at all.
         */}
        <p className="sheet-who">
          {email ? t('session.as').replace('{email}', email) : t('session.signedIn')}
        </p>

        {/*
         * Language and the door out, under a rule and away from the destinations.
         *
         * The language switch used to sit in the bar, where it competed for the width
         * the chat button now takes. It is a setting somebody touches once, and a menu
         * is where a setting somebody touches once belongs.
         */}
        <div className="sheet-foot">
          <LocaleSwitch />
          <button type="button" className="linky" onClick={onSignOut}>
            {t('session.signOut')}
          </button>
        </div>
      </nav>
    </>
  );
}

function Shell({
  children,
  session,
  onChanged,
}: {
  children: React.ReactNode;
  /** `undefined` until the cookie has been asked about. See `Routed`. */
  session: Session | undefined;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  /*
   * One answer to "who is looking", shared with every route below (F-139).
   *
   * The bar, the menu and the footer used to read `session.signedIn` here while `/entrar`
   * read nothing at all, which is how a sign-in form came to be rendered under a working
   * menu. See `session.ts`.
   */
  const viewer = viewerOf(session);
  const signedIn = showsSignedInChrome(viewer);
  const email = signedInAs(session);

  /*
   * Choosing a destination closes the menu, and the menu does not survive the
   * navigation. There is nothing to be gained by leaving it up over the page it was
   * used to reach, and standalone there is no back gesture to dismiss it with.
   */
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Escape and the scrim put focus back on the control that opened the menu. Without
  // it a keyboard user is returned to the top of the document instead.
  const dismiss = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);


  /*
   * The hidden switch, and the two refs are what keep it from misfiring.
   *
   * `held` is set when the timer fires and read by the click the browser sends afterwards, so
   * the navigation is suppressed exactly once. A hold that toggled the diagnostics must not
   * also take somebody home, and the click cannot be prevented from inside the timer because
   * it has not happened yet.
   */
  const holdTimer = useRef<number>(0);
  const held = useRef(false);
  const [debugSaid, setDebugSaid] = useState<string>();

  const startHold = useCallback(() => {
    held.current = false;
    window.clearTimeout(holdTimer.current);
    holdTimer.current = window.setTimeout(() => {
      held.current = true;
      const now = setDebug(!debugOn());
      setDebugSaid(now ? 'diagnostics on' : 'diagnostics off');
      window.setTimeout(() => setDebugSaid(undefined), 1600);
    }, DEBUG_HOLD_MS);
  }, []);

  const cancelHold = useCallback(() => {
    window.clearTimeout(holdTimer.current);
  }, []);

  const onMarkClick = useCallback((event: React.MouseEvent) => {
    if (!held.current) return;
    held.current = false;
    event.preventDefault();
  }, []);

  return (
    <>
      {debugSaid && (
        <p className="debug-said" role="status">
          {debugSaid}
        </p>
      )}
      {/*
       * Fixed, not sticky, and outside the scrolling column entirely.
       *
       * "Always visible" is the requirement and `sticky` does not meet it: a sticky
       * header inside a scroll container that is itself the page will stay put, but the
       * chat gives its thread its own scroller, and a header that scrolls with the
       * document leaves the menu somewhere above a fortnight of messages. Its cost to
       * the page is one constant — `--bar-h` — which `.shell` reserves.
       */}
      <header className="topbar">
        <div className="topbar-inner">
          {signedIn && (
            <button
              ref={buttonRef}
              type="button"
              className="hamburger"
              aria-expanded={open}
              aria-controls="menu"
              onClick={() => (open ? dismiss() : setOpen(true))}
            >
              <span className="bars" aria-hidden="true" />
              <span className="sr-only">{t('nav.sections')}</span>
            </button>
          )}
          <Link
            to="/"
            className="mark"
            aria-label={t('nav.home')}
            // Held down, this toggles the diagnostics — see `debug.ts`. A hold rather than a
            // count of taps, because this is a link and five taps would navigate five times.
            onPointerDown={startHold}
            onPointerUp={cancelHold}
            onPointerLeave={cancelHold}
            onPointerCancel={cancelHold}
            onClick={onMarkClick}
            /*
             * Belt and braces with `-webkit-touch-callout: none` in the stylesheet. Some iOS
             * versions raise a `contextmenu` for a long press even where the callout is
             * suppressed, and it arrives *before* the hold completes — which cancels the
             * gesture rather than merely showing a sheet over it.
             */
            onContextMenu={(event) => event.preventDefault()}
          >
            nabu
          </Link>
          {signedIn ? (
            /*
             * The chat, in the bar, on every screen. The requirement is one tap from
             * anywhere and a menu is two — and this is where the learner spends nearly
             * all of their time, so it is the one destination that has earned the width.
             * On the right because that is the reachable top corner for most hands; the
             * hamburger takes the left, where every app puts it.
             */
            <NavLink to="/chat" className="chat-tab">
              {t('nav.chat')}
            </NavLink>
          ) : (
            /* Signed out there is no menu to put it in, and room in the bar for it. */
            <LocaleSwitch />
          )}
        </div>
        {/*
         * Inside the fixed header, not inside the scroller.
         *
         * It was below `.shell` and scrolled away with the page: *"the refresh should be
         * visible always, it should pop up on top and always up above, right now it hides
         * with the scroll."* A notice you have to scroll back to find does not work, and the
         * update one exists precisely because an installed app has nowhere else to say it.
         *
         * The earlier comment worried that a banner appearing inside the bar would change
         * `--bar-h` while somebody was reading. It does, and that is now the point rather
         * than the objection: the page below re-offsets so the notice never covers content.
         * What makes it safe is that the height is a constant — one notice, one known height
         * — so the chat's box arithmetic stays exact instead of needing a measurement.
         */}
        <Notices signedIn={signedIn} />
      </header>

      {/*
       * No menu at all until there is a session.
       *
       * Every destination behind it needs one, so a signed-out visitor offered five
       * sections is offered five trips to the sign-in screen. The marketing page and
       * the sign-in screen keep the bar, the wordmark and the language switch, which is
       * everything either of them can act on.
       */}
      {signedIn && (
        <Menu
          open={open}
          onDismiss={dismiss}
          email={email}
          onSignOut={() => {
            setOpen(false);
            void signOut().then(onChanged);
          }}
        />
      )}

      {/*
       * The scrolling column, and the height contract the chat is built against.
       *
       * `.shell` is a flex column at least as tall as the viewport minus the bar, and
       * `main.stage` is the flexible child. A screen that wants the remaining height
       * rather than growing the page takes it by being a flex item — see `.stage > .fills`
       * in styles.css. Nothing here measures anything in JavaScript, which is what keeps
       * it correct when the on-screen keyboard shrinks the viewport.
       */}
      <div className="shell">
        <ScrollToTop />
        <OpenFromNotification />
        <main className="stage">{children}</main>
        {/*
         * The footer belongs to the public site, not to the app.
         *
         * Signed in this is a phone app with no browser chrome, and a strip saying where
         * it was built under a conversation is both noise and, for the chat, stolen
         * height: `main` can only hand a screen the rest of the viewport if nothing is
         * queued underneath it.
         */}
        {!signedIn && (
          <footer className="bar foot">
            <span>{t('footer.built')}</span>
            <span className="muted">nabu.guru</span>
          </footer>
        )}
      </div>
    </>
  );
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <li className="step">
      <span className="step-n" aria-hidden="true">
        {n}
      </span>
      <div>
        <h3>{title}</h3>
        <p>{body}</p>
      </div>
    </li>
  );
}

/**
 * The public front page, which only a signed-out visitor ever sees.
 *
 * A signed-in learner is redirected off it before it renders — see the `/` route — so
 * the call to action has two states rather than three now: waiting for the answer, and
 * the way in.
 */
function Home({ session }: { session: Session | undefined }) {
  const { t } = useI18n();
  return (
    <>
      <section className="hero">
        <h1>{t('home.tagline')}</h1>
        <p className="lede">{t('home.lede')}</p>
      </section>

      <section className="panel">
        <h2>{t('home.how.title')}</h2>
        <ol className="steps">
          <Step n={1} title={t('home.how.1.title')} body={t('home.how.1.body')} />
          <Step n={2} title={t('home.how.2.title')} body={t('home.how.2.body')} />
          <Step n={3} title={t('home.how.3.title')} body={t('home.how.3.body')} />
        </ol>
      </section>

      <section className="panel">
        <h2>{t('home.why.title')}</h2>
        <p>{t('home.why.body')}</p>
      </section>

      <section className="panel cta">
        <h2>{t('home.cta.title')}</h2>
        <p>{t('home.cta.body')}</p>
        {/* Nothing at all until the answer is in. Somebody who is signed in is about
            to be sent to the chat, and a "sign in" button rendered at them in the
            half-second before that is a button that lies. */}
        {session === undefined ? (
          <p className="muted">{t('session.checking')}</p>
        ) : (
          <Link className="button primary" to={ROUTES.signIn}>
            {t('home.cta.button')}
          </Link>
        )}
      </section>
    </>
  );
}

/**
 * The quiet wait, and the reason it is not the sign-in screen.
 *
 * `undefined` means "not asked yet", which is a different thing from signed out. The
 * session cookie is HttpOnly by design, so the page genuinely cannot know without
 * asking — and a flash of "you are not logged in" at somebody who is is alarming in a
 * way a moment of blankness is not. This is the branch that bug lived in, and it now
 * guards five screens rather than two, which is the other reason it is a component.
 */
function Guarded({
  session,
  onSignedIn,
  children,
}: {
  session: Session | undefined;
  /**
   * The same awaitable signature `SignInRoute` declares, and not `() => void`.
   *
   * This is the path *most* sign-ins take — every learner who followed a link into a
   * guarded screen rather than to the sign-in route — so a narrower type here would have
   * allowed a synchronous callback on the common path while the rare one was awaited. The
   * sign-in screen decides what to say from what this returns; a caller that returned
   * nothing would make it say the wrong thing.
   */
  onSignedIn: () => Promise<Session | undefined>;
  children: React.ReactNode;
}) {
  const { t } = useI18n();
  const view = guardedView(viewerOf(session));
  if (view === 'waiting') {
    return (
      <section className="panel">
        <p className="muted">{t('session.checking')}</p>
      </section>
    );
  }
  if (view === 'signIn') return <SignIn onSignedIn={onSignedIn} />;
  return <>{children}</>;
}

/**
 * The sign-in screen as a route, which is where F-139 happened.
 *
 * Leo: *"i got a refresh notice at that state and it brought me back to login screen BUT
 * with a logged in menu i could go to my plan and describe it."* This route was mounted
 * bare — `<SignIn />`, no guard, no question asked — so it handed a form to anybody who
 * asked for it, signed in or not. Reloading an installed app that was sitting here (which
 * is where signing in used to leave you, telling you to reload) produced exactly that: a
 * login form under a menu full of the sections you were signed in to. The session was
 * fine the whole time. Only this route disagreed with it.
 *
 * Somebody already signed in is sent to the landing instead, and `replace` keeps this out
 * of the history behind them — otherwise the back gesture lands on a form that bounces.
 */
function SignInRoute({
  session,
  onSignedIn,
}: {
  session: Session | undefined;
  onSignedIn: () => Promise<Session | undefined>;
}) {
  const { t } = useI18n();
  const view = signInView(viewerOf(session));

  if (view === 'waiting') {
    // The same quiet wait as the guard, and for the same reason: the cookie is HttpOnly,
    // so "not asked yet" is not "signed out" and must not be drawn as one.
    return (
      <section className="panel">
        <p className="muted">{t('session.checking')}</p>
      </section>
    );
  }
  if (view === 'elsewhere') return <Navigate to={ROUTES.home} replace />;
  return <SignIn onSignedIn={onSignedIn} />;
}

/**
 * An address that used to be a screen, sent to the one that replaced it (F-142).
 *
 * The query string and the fragment come along, which is not ceremony. Fragments name
 * panels now (F-143), so a link to `/ajustes#notifications` — nothing minted one before
 * today, but an address bar is copyable and these paths are going to outlive this week —
 * has to arrive at the notification switch rather than at the top of the configuration
 * screen, which is F-143's complaint reintroduced by the fix for F-142.
 */
function Moved({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
}

/** Progress: where you are. One panel, which is the whole of this section. */
function ProgressPage() {
  const { t } = useI18n();
  return (
    <Disclosure id="progress" title={t('prog.title')} repeatsTitle>
      <Progress />
    </Disclosure>
  );
}

/**
 * Configuration: the target, when nabu may reach you, and notifications.
 *
 * Three sections from three places. The target comes off the objective and so is
 * rendered by `Objectives`, which owns loading it; the other two belong to the learner
 * and render whether or not anything is running. They are adjacent rather than one
 * panel because quiet hours and notifications are read as a pair — a device that
 * notified outside the hours somebody chose would contradict them — and the pairing is
 * this page's job now that neither is buried in the objectives screen.
 */
function ConfigurationPage() {
  const { t } = useI18n();
  return (
    <>
      <Objectives section="settings" />
      {/*
       * Both panels are addressable and only one of them is linked to today (F-143): the
       * setup notice offers the schedule, and the notification switch is addressable because
       * it is the other half of "nabu cannot reach you" and costs one word to name properly.
       *
       * The `anchor` is the learner's word and the `id` is the storage key their folded
       * sections are remembered under — see `Disclosure`, where changing the second one is
       * what would spring a section somebody folded away back open.
       */}
      <Disclosure id="window" anchor="schedule" title={t('win.title')} repeatsTitle>
        <Schedule />
      </Disclosure>
      <Disclosure id="push" anchor="notifications" title={t('push.title')} repeatsTitle>
        <Push />
      </Disclosure>
    </>
  );
}

/**
 * Profile: how you get in, your email, and the door out.
 *
 * **It used to be "your number", named and not shown.** That was a gap rather than a
 * decision — nothing the browser can call returns it — and F-103 turned it into a lie as
 * well: an account signs in with a code mailed to an address, and questions arrive in the
 * chat rather than on a phone number. What is left says how you get in and how you are
 * reached, both of which are true and neither of which needs a row read.
 *
 * The section's `id` is deliberately still `number`. It is the `localStorage` key the fold
 * is remembered under (see `Disclosure`), so renaming it would silently re-open a section
 * somebody had folded away — copy changes, storage keys do not.
 */
function ProfilePage({
  session,
  onChanged,
}: {
  session: Session | undefined;
  onChanged: () => void;
}) {
  const { t } = useI18n();
  const email = signedInAs(session);
  return (
    <>
      <Disclosure id="number" title={t('profile.account.title')}>
        <section className="panel">
          {/*
           * The account, named, at the top of the screen about the account (F-140).
           *
           * The menu says it too, one tap from anywhere; this is where somebody comes when
           * the menu was not enough — to check the address before adding a second one
           * below, or before deleting everything at the bottom of this page.
           */}
          <p className="profile-who">
            {email ? t('session.as').replace('{email}', email) : t('session.signedIn')}
          </p>
          <p className="muted">{t('profile.account.body')}</p>
          <div className="actions">
            <button
              type="button"
              className="button"
              onClick={() => void signOut().then(onChanged)}
            >
              {t('session.signOut')}
            </button>
          </div>
        </section>
      </Disclosure>

      <Disclosure id="email" title={t('email.title')} repeatsTitle>
        {/* Re-reads the session when an address is linked, so the line above says the
            address that now signs this account in rather than the one that used to. */}
        <EmailAccess onLinked={onChanged} />
      </Disclosure>

      {/* Renders nothing until there is an address, and the section hides with it. */}
      <Disclosure id="mail" title={t('mail.title')} repeatsTitle>
        <Mail />
      </Disclosure>

      {/*
       * Shut by default, alone among every section in the app.
       *
       * It is the one irreversible control in the system, and this page is now a short
       * scroll rather than the bottom of a screen nobody reached by accident. Folded
       * away it takes a deliberate tap to see at all, which is the cheapest possible
       * guard and costs somebody who came here to leave exactly one tap.
       */}
      <Disclosure id="delete" title={t('del.title')} open={false} repeatsTitle>
        <DeleteAccount />
      </Disclosure>
    </>
  );
}

function Routed() {
  const [session, setSession] = useState<Session>();

  /*
   * Returns the session it just read, which is what lets the sign-in screen wait for the
   * answer rather than telling a learner to reload (F-138).
   *
   * **The answer and not the promise of a re-render.** Handing back only a promise made the
   * sign-in screen ask "am I still mounted?" instead, which is a question about React's
   * scheduling rather than about the product — and it had the wrong answer, because a
   * default-lane `setSession` has not re-rendered anything by the time this resolves.
   */
  const refresh = useCallback(
    () =>
      whoAmI().then((session) => {
        setSession(session);
        return session;
      }),
    [],
  );

  // Asked once on load. This is what makes a reload keep you signed in.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const guard = (element: React.ReactNode) => (
    <Guarded session={session} onSignedIn={refresh}>
      {element}
    </Guarded>
  );

  return (
    <Shell session={session} onChanged={refresh}>
      <Routes>
        {/*
         * What `/` is depends on who is asking and where from.
         *
         * A signed-in learner lands on the conversation, wherever they came from: the
         * manifest's `start_url` is `/`, so this is the route the home-screen icon
         * opens, and the chat is where they spend their time.
         *
         * Signed out, an installed app goes straight to the sign-in screen and a
         * browser gets the front page. Same route, and the difference is real: nobody
         * installs an app to read the pitch, and a visitor who followed a link has not
         * been asked to sign in to anything yet.
         */}
        <Route
          path={ROUTES.home}
          element={
            viewerOf(session) === 'signedIn' ? (
              <Landing signedIn />
            ) : STANDALONE ? (
              guard(null)
            ) : (
              <Home session={session} />
            )
          }
        />
        <Route
          path={ROUTES.signIn}
          element={<SignInRoute session={session} onSignedIn={refresh} />}
        />

        {/* The conversation (F-100), and the landing screen. */}
        <Route path={ROUTES.chat} element={guard(<Chat />)} />

        {/* ---- The five sections ---- */}
        <Route path={ROUTES.progress} element={guard(<ProgressPage />)} />
        <Route path={ROUTES.plan} element={guard(<Objectives section="plan" />)} />
        <Route path={ROUTES.research} element={guard(<Objectives section="research" />)} />
        <Route path={ROUTES.settings} element={guard(<ConfigurationPage />)} />
        <Route
          path={ROUTES.profile}
          element={guard(<ProfilePage session={session} onChanged={refresh} />)}
        />

        {/*
         * Every address that used to be one of the above (F-142), and the screen the five
         * sections were cut out of before that.
         *
         * Redirects rather than deletions: they are in browser histories, in home screens
         * saved before today, and in anything anybody has bookmarked. A 404 at a URL that
         * worked yesterday is indistinguishable from the app being broken.
         *
         * **Generated from the one table in `routes.ts`, and that generation is the whole
         * guard.** There is nothing else holding it: `App.tsx` is not in the test-compiled
         * set and this repository has no DOM harness, so no test can see whether this file
         * mounts anything at all — deleting these three lines leaves the suite green.
         * `routes.test.ts` checks that the *table* is coherent (no dead path is also live,
         * every target is a real route); the mapping over it is what makes a rename carry
         * its redirect without anybody remembering to.
         */}
        {Object.entries(MOVED).map(([gone, now]) => (
          <Route key={gone} path={gone} element={<Moved to={now} />} />
        ))}

        {/*
          Where a one-click unsubscribe link lands (F-106).

          **Mounted with no session guard at all**, unlike every other route above. Somebody
          stopping our mail must not have to sign in to do it — an unsubscribe that asks for
          a password is how a person reaches for the spam button instead, and a complaint
          costs the sending domain far more than an opt-out does. The token in the query
          string is the authorisation, and the API refuses to act on one it did not mint.

          **The one path F-142 did not rename**, and the only Spanish one left. It is not a
          screen anybody navigates to: the API mints it into the footer and the
          `List-Unsubscribe` header of every message it sends, so the addresses that matter
          are already in inboxes and cannot be edited after the fact. Renaming it would
          break those or leave two spellings of it for ever, and nobody reads this one out
          of an address bar.
        */}
        <Route path={ROUTES.unsubscribe} element={<Unsubscribe />} />
        {/*
          The console. Mounted for everybody, because whether it exists is not a
          question the browser can answer — `/api/admin/me` decides, and a
          non-operator gets the same `NotFound` as any wrong URL (D-20). Matched as
          `/admin/*` so the console owns its own subpaths without App knowing them.
        */}
        <Route path="/admin/*" element={<Console />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Shell>
  );
}

export default function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <Routed />
      </BrowserRouter>
    </I18nProvider>
  );
}
